import BrainIcon from "~/components/icons/BrainIcon/BrainIcon";
import CartIcon from "~/components/icons/CartIcon/CartIcon";
import { ProjectCard, type ProjectCardData } from "~/modules/dashboard/components/ProjectCard.tsx";
import { ProjectCardPlaceholder } from "~/modules/dashboard/components/ProjectCardPlaceholder.tsx";
import { TypeCard } from "~/modules/dashboard/components/TypeCard.tsx";
import { useActiveStaffings } from "~/modules/dashboard/hooks/useActiveStaffings.ts";
import { useDashboardUser } from "~/modules/dashboard/hooks/useDashboardUser.tsx";
import { EmploymentTypeId } from "~/modules/humanResources/api/employmentType/employmentTypeTypes.ts";
import type { UsersActiveStaffingType } from "~/modules/project/api/staffing/staffingTypes.ts";
import { TimeTrackingTypeId } from "~/modules/timeTracking/api/timeTrackingType/timeTrackingTypeTypes.ts";

function getCards(projects: UsersActiveStaffingType[]): ProjectCardData[] {
	return projects.flatMap((project) =>
		project.projectPhases.flatMap((phase) =>
			phase.orders.flatMap((order) => order.staffings.map((staffing) => ({
				project,
				phaseNumber: phase.phaseNumber,
				order,
				staffing,
			})))));
}

const TYPE_CARDS = [
	{ typeId: TimeTrackingTypeId.Sales, title: "Sales", icon: <CartIcon className="size-5" /> },
	{ typeId: TimeTrackingTypeId.Internal, title: "Intern", icon: <BrainIcon className="size-5" /> },
];

type Props = {
	/** The tracked minutes of the shown month for each staffing id. */
	trackedMinutesByStaffing: Map<string, number>;
	/** The tracked minutes of the shown month for each time tracking type id. */
	trackedMinutesByType: Map<string, number>;
	/** The first read of the trackings or of the closing of the month. Until it ends, only the placeholder card shows. */
	isFirstLoad: boolean;
	/** The trackings of the new month load. The cards stay, and their values show a spinner. */
	isChangingMonth: boolean;
};

// One card for each staffing, not for each project: two staffings of one project give two cards.
// A staffing that ended in this month keeps its card until the month closing, because it still takes
// trackings for the days of its period until then.
// The projects have no heading. From `md`, they are one row that scrolls sideways.
// A user who is not a freelancer also gets a Sales and an Intern card after the project cards. The empty
// text shows only when no card shows at all. The load, the empty text and a failed read show in a card of
// the project card height, so the section does not jump. The cards show only when all reads of the first load
// have answered, so no card pops in alone, and no value shows a spinner right after its card.
export function ProjectCards({ trackedMinutesByStaffing, trackedMinutesByType, isFirstLoad, isChangingMonth }: Props) {
	const { data, isPending, isError } = useActiveStaffings();
	const user = useDashboardUser();
	// No user yet counts as a load, so a freelancer never sees the type cards flash while the user loads.
	const isLoading = isPending || !user || isFirstLoad;
	const cards = data && !isLoading ? getCards(data.projects) : [];
	const typeCards = !isLoading && user.employmentTypeId !== EmploymentTypeId.Freelancer ? TYPE_CARDS : [];
	const cardCount = cards.length + typeCards.length;

	return (
		<section aria-label="Projekte">
			<ul className="grid grid-cols-1 gap-3 md:flex md:overflow-x-auto md:pb-3 md:[&>li]:w-80 md:[&>li]:shrink-0">
				{/* From `md`, the cards are one row, so 3 placeholders fill it. The phone list has one column and shows 1. */}
				{isLoading && [0, 1, 2].map((index) => (
					<ProjectCardPlaceholder key={index}
											className={index > 0 ? "max-md:hidden" : undefined} />
				))}
				{!isLoading && isError && <ProjectCardPlaceholder message="Die Projekte konnten nicht geladen werden." />}
				{cards.map((card) => <ProjectCard key={card.staffing.id}
												  trackedMinutes={trackedMinutesByStaffing.get(card.staffing.id) ?? 0}
												  trackedMinutesAreLoading={isChangingMonth}
												  {...card} />)}
				{typeCards.map((typeCard) => (
					<TypeCard key={typeCard.typeId}
							  title={typeCard.title}
							  icon={typeCard.icon}
							  trackedMinutes={trackedMinutesByType.get(typeCard.typeId) ?? 0}
							  trackedMinutesAreLoading={isChangingMonth} />
				))}
				{data && !isLoading && cardCount === 0 && <ProjectCardPlaceholder message="Du bist aktuell keinem Projekt zugeordnet." />}
			</ul>
		</section>
	);
}
